/* eslint-disable no-useless-constructor */
import _ from 'lodash';
import React, { Component } from 'react';
import { Row, Col, Button, Form, Modal, notification } from '@duxton-web/ui';
import { ArrowLeftUniversal } from '@duxton-web/icons';
import SegmentDetailDisplay from 'omega-rtc/views/trident-campaigns/review/SegmentDetailDisplay';
import JourneyBuilderPanel from './Panel';
import JourneyBuilderDiagram from './Diagram';
import './css/main.scss';
import dayjs from 'dayjs';

const FIRST_NODE_INDEX = 1;

type Props = {
  data: object,
  isView: boolean,
  handleSubmitForm: Function,
  campaignView: object,
  goBack: Function,
  showMetrics: Boolean,
  isShowApproval: Boolean,
};

type State = {
  selectedNode: any,
  nextNodes: any,
  hasError: Boolean,
};

class JourneyBuilder extends Component {
  formRef = React.createRef();
  value = {};
  props: Props;
  state: State;
  constructor(props: Props) {
    super(props);
    this.state = {
      diagramData: props.data,
      isModalOpen: false,
      hasError: false,
    };
  }

  showSegmentModal = () => {
    this.setState({
      isModalOpen: true,
    });
  };
  handleOk = () => {
    this.setState({
      isModalOpen: false,
    });
  };
  handleCancel = () => {
    this.setState({
      isModalOpen: false,
    });
  };

  onNodeClick = node => {
    const data = this.state.selectedNode
      ? this.state.diagramData
      : this.props.data;
    const nodeData = data[node.id];
    const nextNodes = [];

    // Preparing next nodes's data for UI display
    _.forEach(nodeData.children, nodeId => {
      nextNodes.push({
        id: nodeId,
        ...data[nodeId],
      });
    });

    this.setState({
      selectedNode: { ...nodeData, id: node.id },
      nextNodes,
    });

    setTimeout(() => {
      this.validateFormError();
    }, 10);
  };

  initialize() {
    const { data } = this.props;
    this.formRef.current.setFieldsValue(this.props.data);
    _.forEach(data, (nodeData, nodeId) => {
      this.formRef.current.setFieldValue(nodeId, nodeData);
    });

    /**
     * First node will be seleted by default
     */
    const firstNode = data[FIRST_NODE_INDEX];
    if (!_.isEmpty(data)) {
      this.onNodeClick({ ...firstNode, id: FIRST_NODE_INDEX });
    }

    setTimeout(() => {
      this.validateFormError();
    }, 10);
  }

  componentDidMount() {
    this.initialize();
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(this.props.data, prevProps.data)) {
      this.initialize();
      this.updateDiagramData(this.props.data);
    }
  }

  updateDiagramData(data) {
    this.setState({
      diagramData: data,
    });
  }

  validateFormError = () => {
    if (this.formRef && this.formRef.current) {
      this.formRef.current
        .validateFields()
        .then(() => {
          this.setState({ hasError: false });
        })
        .catch(err => {
          this.setState({
            hasError: _.get(err, 'errorFields', []).length,
          });
        });
    }
  };

  onValuesChange = () => {
    this.validateFormError();
    this.setState({
      diagramData: this.formRef.current.getFieldsValue(true),
    });
  };

  /**
   * refreshDiagram: to manually trigger the diagram canvas
   */
  refreshDiagram = () => {
    this.onValuesChange();
  };

  onFinishFailed = () => {
    this.openNotification();
  };

  validate = (values, callback) => {
    const startTime = dayjs(_.get(values, '1.data.schedule.startTime', ''));
    const endTime = dayjs(_.get(values, '1.data.schedule.endTime', ''));
    const frequency = _.get(values, '1.data.schedule.frequency', '');
    if (frequency !== 'nr' && startTime > endTime) {
      this.openNotification();
    } else {
      callback();
    }
  };

  // populate the offsetDay and offsetWeek based on the startTime
  // this is to make sure the offsetDay and offsetWeek are always in sync with the startTime
  // in case mt: offsetDay should be the day of the month
  // in case wk: offsetDay should be the day of the week
  populateOffsets = values => {
    const frequency = _.get(values, '1.data.schedule.frequency', '');
    if (frequency === 'mt') {
      _.set(values, '1.data.schedule.monthlyFrequency', 'day');
    }
    return values;
  };

  sendApproval = () => {
    this.formRef.current
      .validateFields()
      .then(() => {
        const formData = this.formRef.current.getFieldsValue(true);
        this.validate(formData, () => {
          this.props.handleSubmitForm(this.populateOffsets(formData), true);
        });
      })
      .catch(() => {
        this.openNotification();
      });
  };

  sendDraft = () => {
    this.formRef.current
      .validateFields()
      .then(() => {
        const formData = this.formRef.current.getFieldsValue(true);
        this.validate(formData, () => {
          this.props.handleSubmitForm(this.populateOffsets(formData), false);
        });
      })
      .catch(() => {
        this.openNotification();
      });
  };

  getSegmentSourceID = () => {
    if (this.formRef.current) {
      return this.formRef.current.getFieldValue([
        '1',
        'data',
        'userSource',
        'sourceID',
      ]);
    }
    return '';
  };

  openNotification = () => {
    const key = `open${Date.now()}`;
    const btn = (
      <Button
        type="primary"
        size="small"
        onClick={() => notification.destroy(key)}
      >
        Okay
      </Button>
    );
    notification.open({
      message: <b style={{ fontSize: 20 }}>Journey is not completed</b>,
      description: (
        <span style={{ color: '#3D3D3D' }}>
          We notice you haven’t completed all the required fields for this
          journey. Please check all the steps with a red error icon and submit
          again.
        </span>
      ),
      btn,
      key,
      duration: 0,
    });
  };

  render() {
    const { isView, campaignView, isShowApproval } = this.props;
    const { selectedNode, diagramData, nextNodes, isModalOpen } = this.state;
    const campaignName = _.get(campaignView, 'campaign.name', '');
    const segment = this.getSegmentSourceID();
    return (
      <div className="jb-widget">
        <Modal
          width={1016}
          title="User Segment Breakdown"
          open={isModalOpen}
          onCancel={this.handleCancel}
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { display: 'none' } }}
        >
          <div style={{ marginTop: 16 }} className="segmentDetail">
            <SegmentDetailDisplay
              segment={segment}
              targetUserType={'passenger'}
              countryID={_.get(campaignView, 'countryID')}
            />
          </div>
        </Modal>

        <Form
          layout="vertical"
          onFinish={this.sendApproval}
          onFinishFailed={this.onFinishFailed}
          onValuesChange={this.onValuesChange}
          ref={this.formRef}
        >
          {!isView && (
            <Row className="header" align={'middle'}>
              <Col span={8}>
                <Button
                  icon={<ArrowLeftUniversal />}
                  onClick={this.props.goBack}
                  type="text"
                >
                  Back
                </Button>
              </Col>
              <Col span={8} className="campaignName">
                {campaignName}
              </Col>
              <Col span={8}>
                <Row style={{ float: 'right' }}>
                  <Col>
                    <Button onClick={this.sendDraft} type="default">
                      Save as draft
                    </Button>
                  </Col>
                  {isShowApproval && (
                    <Col>
                      <Button
                        htmlType="submit"
                        style={{ marginLeft: 3 }}
                        type="primary"
                      >
                        Submit For Approval
                      </Button>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          )}
          <Row style={{ minHeight: 400 }}>
            <Col className="left-panel" span={isView ? 24 : 17}>
              <JourneyBuilderDiagram
                data={diagramData}
                onNodeClick={this.onNodeClick}
                showMetrics={this.props.showMetrics}
                isView={isView}
              />
            </Col>
            {!isView && (
              <Col className="right-panel" span={7}>
                <JourneyBuilderPanel
                  showSegmentModal={this.showSegmentModal}
                  form={this.formRef}
                  node={selectedNode}
                  nextNodes={nextNodes}
                  hasError={this.state.hasError}
                  refreshDiagram={this.refreshDiagram}
                />
              </Col>
            )}
          </Row>
        </Form>
      </div>
    );
  }
}

export default JourneyBuilder;
